// components/Portfolio.jsx — Swiper.js horizontal carousel with modal gallery

import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Mousewheel, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { SectionWrapper, SectionTitle, Badge, Modal, Button } from "./UI";

// ── DeviceFrame ───────────────────────────────────────────────────────────────
function DeviceFrame({ src, alt, device, dark, isEs }) {
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = (e) => { if (e.target.scrollTop > 10) setScrolled(true); };

  if (device === "mobile") {
    return (
      <div className="flex items-center justify-center py-4 px-8">
        <div className={`relative rounded-[2.5rem] border-[6px] shadow-2xl overflow-hidden ${dark ? "border-slate-600 bg-black" : "border-slate-400 bg-black"}`} style={{ width: 260, height: 520 }}>
          <div className="w-full h-full overflow-y-auto overflow-x-hidden scrollbar-thin" onScroll={handleScroll}>
            <img src={src} alt={alt} className="w-full object-cover object-top" draggable={false} />
          </div>
          {!scrolled && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none">
              <span className={`text-xs font-medium ${dark ? "text-white/60" : "text-white/80"}`}>scroll</span>
              <div className="flex flex-col items-center gap-0.5 animate-bounce">
                <div className="w-0.5 h-3 bg-white/60 rounded-full" />
                <div className="w-0 h-0 border-l-[4px] border-r-[4px] border-t-[5px] border-l-transparent border-r-transparent border-t-white/60" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center py-4 px-4">
      <div className={`relative rounded-xl border-[6px] shadow-2xl overflow-hidden w-full ${dark ? "border-slate-600 bg-black" : "border-slate-400 bg-black"}`} style={{ height: 420 }}>
        <div className="w-full h-full overflow-y-auto overflow-x-hidden scrollbar-thin" onScroll={handleScroll}>
          <img src={src} alt={alt} className="w-full object-cover object-top" draggable={false} />
        </div>
        {!scrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)" }}>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2">
              <span className="text-white/80 text-xs font-medium">{isEs ? "Desplaza para explorar" : "Scroll to explore"}</span>
              <div className="flex flex-col items-center animate-bounce">
                <div className="w-0.5 h-2.5 bg-white/70 rounded-full" />
                <div className="w-0 h-0 border-l-[3px] border-r-[3px] border-t-[4px] border-l-transparent border-r-transparent border-t-white/70" />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={`w-16 h-3 rounded-b-lg ${dark ? "bg-slate-600" : "bg-slate-300"}`} />
      <div className={`w-24 h-2 rounded-lg ${dark ? "bg-slate-600" : "bg-slate-300"}`} />
    </div>
  );
}

// ── PortfolioModal ────────────────────────────────────────────────────────────
function PortfolioModal({ projects, initialProjectIdx, onClose, t, dark, lang }) {
  const [projectIdx, setProjectIdx] = useState(initialProjectIdx);
  const [imageIdx, setImageIdx]     = useState(0);
  const [device, setDevice]         = useState("desktop");
  const isEs = lang === "es";

  const project = projects[projectIdx];
  if (!project) return null;

  const title       = isEs ? (project.titleEs       || project.title)       : project.title;
  const description = isEs ? (project.descriptionEs || project.description) : project.description;
  const category    = isEs ? (project.categoryEs    || project.category)    : project.category;

  const images       = project.images || [];
  const currentImage = images[imageIdx] || {};
  const imgSrc       = device === "mobile" ? (currentImage.mobile || currentImage.desktop) : (currentImage.desktop || currentImage.mobile);
  const imgAlt       = currentImage.alt || title;

  const prevImage   = () => setImageIdx((i) => Math.max(0, i - 1));
  const nextImage   = () => setImageIdx((i) => Math.min(images.length - 1, i + 1));
  const prevProject = () => { setProjectIdx((i) => Math.max(0, i - 1)); setImageIdx(0); setDevice("desktop"); };
  const nextProject = () => { setProjectIdx((i) => Math.min(projects.length - 1, i + 1)); setImageIdx(0); setDevice("desktop"); };

  return (
    <Modal open onClose={onClose}>
      <div className={`rounded-2xl overflow-hidden max-h-[92vh] overflow-y-auto ${dark ? "bg-[#2B3B55] text-white" : "bg-white text-[#0F172A]"}`}>
        <button onClick={onClose} className="absolute -top-4 -right-4 z-20 w-9 h-9 rounded-full bg-white text-[#0F172A] flex items-center justify-center text-sm font-bold shadow-lg hover:bg-[#A3E635] transition-colors">✕</button>

        <div className={`relative ${dark ? "bg-white/[0.03]" : "bg-[#E2E8F0]/30"} min-h-[480px] flex items-center justify-center`}>
          <div className="w-full">
            <DeviceFrame src={imgSrc} alt={imgAlt} device={device} dark={dark} isEs={isEs} />
          </div>
        </div>

        <div className={`p-4 border-t ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="min-w-0">
              <Badge className="mb-1">{category}</Badge>
              <h3 className="font-bold text-base truncate">{title}</h3>
              <p className={`text-xs mt-0.5 line-clamp-1 ${dark ? "text-white/45" : "text-[#0F172A]/45"}`}>{description}</p>
            </div>

            <div className="flex items-center gap-3 shrink-0 flex-wrap justify-end">
              <div className={`flex rounded-xl overflow-hidden border ${dark ? "border-white/15" : "border-[#E2E8F0]"}`}>
                <button onClick={() => setDevice("desktop")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${device === "desktop" ? "bg-[#A3E635] text-[#0F172A]" : dark ? "text-white/50 hover:text-white" : "text-[#0F172A]/50 hover:text-[#0F172A]"}`}>
                  🖥 Desktop
                </button>
                <button onClick={() => setDevice("mobile")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${device === "mobile" ? "bg-[#A3E635] text-[#0F172A]" : dark ? "text-white/50 hover:text-white" : "text-[#0F172A]/50 hover:text-[#0F172A]"}`}>
                  📱 Mobile
                </button>
              </div>

              {images.length > 1 && (
                <div className="flex gap-1.5 items-center">
                  {images.map((_, i) => (
                    <button key={i} onClick={() => setImageIdx(i)} className={`h-2 rounded-full transition-all duration-200 ${i === imageIdx ? "bg-[#A3E635] w-4" : `w-2 ${dark ? "bg-white/30" : "bg-[#0F172A]/20"} hover:bg-[#A3E635]/60`}`} />
                  ))}
                </div>
              )}

              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" onClick={prevImage} disabled={imageIdx === 0} className={dark ? "border-white/20 text-white/60" : ""}>{t.modal.prevProject}</Button>
                <Button variant="outline" size="sm" onClick={nextImage} disabled={imageIdx === images.length - 1} className={dark ? "border-white/20 text-white/60" : ""}>{t.modal.nextProject}</Button>
              </div>
            </div>
          </div>

          {/* Project nav */}
          <div className={`mt-3 pt-3 border-t flex justify-between ${dark ? "border-white/10" : "border-[#E2E8F0]"}`}>
            <Button variant="outline" size="sm" onClick={prevProject} disabled={projectIdx === 0} className={dark ? "border-white/20 text-white/60" : ""}>{t.modal.prevImage}</Button>
            <span className={`text-xs self-center ${dark ? "text-white/30" : "text-[#0F172A]/30"}`}>{projectIdx + 1} / {projects.length}</span>
            <Button variant="outline" size="sm" onClick={nextProject} disabled={projectIdx === projects.length - 1} className={dark ? "border-white/20 text-white/60" : ""}>{t.modal.nextImage}</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Portfolio main ────────────────────────────────────────────────────────────
export default function Portfolio({ content, t, dark, hasBg = false, lang }) {
  const [modalProjectIdx, setModalProjectIdx] = useState(null);
  const isEs = lang === "es";

  const { projects, numberOfCardsToShow = 3, partialCardVisiblePercent = 20 } = content;
  const desktopSlides = numberOfCardsToShow + partialCardVisiblePercent / 100;

  const swiperStyles = `
    .portfolio-swiper .swiper-button-next,
    .portfolio-swiper .swiper-button-prev {
      color: #A3E635;
      background: ${dark ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.9)"};
      width: 2.5rem; height: 2.5rem;
      border-radius: 50%;
      box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    }
    .portfolio-swiper .swiper-button-next::after,
    .portfolio-swiper .swiper-button-prev::after { font-size: 1rem; font-weight: 700; }
    .portfolio-swiper .swiper-button-disabled { opacity: 0.3; }
  `;

  return (
    <SectionWrapper id="portfolio" dark={dark} bg={hasBg ? "alt" : "default"}>
      <style>{swiperStyles}</style>
      <SectionTitle title={t.portfolio.sectionTitle} subtitle={t.portfolio.sectionSubtitle} dark={dark} />

      <Swiper
        className="portfolio-swiper pb-4 select-none !items-stretch"
        modules={[Navigation, Mousewheel, FreeMode]}
        grabCursor navigation
        mousewheel={{ forceToAxis: true }}
        spaceBetween={20}
        breakpoints={{ 0: { slidesPerView: 1.15 }, 640: { slidesPerView: 1.8 }, 768: { slidesPerView: 2.2 }, 1024: { slidesPerView: desktopSlides } }}
      >
        {projects.map((proj, idx) => {
          const title       = isEs ? (proj.titleEs       || proj.title)       : proj.title;
          const description = isEs ? (proj.descriptionEs || proj.description) : proj.description;
          const category    = isEs ? (proj.categoryEs    || proj.category)    : proj.category;

          return (
            <SwiperSlide key={proj.id} className="!h-auto">
              <div onClick={() => setModalProjectIdx(idx)} className={`group relative rounded-2xl border overflow-hidden cursor-pointer h-full flex flex-col transition-all duration-300 hover:border-[#A3E635]/60 hover:shadow-xl hover:shadow-[#A3E635]/5 hover:-translate-y-1 select-none ${dark ? "bg-white/5 border-white/10" : "bg-white border-[#E2E8F0]"}`}>
                {/* overlay روی کل کارت */}
                <div className="absolute inset-[-1px] z-10 rounded-2xl bg-[#0F172A]/0 group-hover:bg-[#0F172A]/80 transition-all duration-300 flex items-center justify-center pointer-events-none">
                  <span className="pointer-events-auto text-sm font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 bg-[#A3E635] text-[#0F172A] px-4 py-2 rounded-xl flex items-center gap-2">
                    {t.portfolio.viewProject}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7"/><path d="M7 7h10v10"/></svg>
                  </span>
                </div>
                <div className="relative overflow-hidden aspect-video flex-shrink-0">
                  <img src={proj.thumbnail || proj.images?.[0]?.desktop || proj.images?.[0]?.mobile || ""} alt={proj.images?.[0]?.alt || title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" draggable={false} />
                  {proj.images?.length > 1 && (
                    <div className="absolute top-2.5 right-2.5 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">{proj.images.length} screens</div>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1 justify-end">
                  <Badge className="mb-2">{category}</Badge>
                  <h3 className="font-bold text-base group-hover:text-[#A3E635] transition-colors">{title}</h3>
                  <p className={`text-xs mt-1.5 line-clamp-3 min-h-[3.6rem] ${dark ? "text-white/45" : "text-[#0F172A]/45"}`}>{description}</p>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {modalProjectIdx !== null && (
        <PortfolioModal projects={projects} initialProjectIdx={modalProjectIdx} onClose={() => setModalProjectIdx(null)} t={t} dark={dark} lang={lang} />
      )}
    </SectionWrapper>
  );
}
